import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  VpnKey as OtpIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';

import axios from 'axios';
import { getToken } from '../../services/authAPIS';

const OTPVerificationModal = ({ open, onClose, onSuccess, reason = 'adding a new role' }) => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(0);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    if (open && !isVerified) {
      handleSendOTP();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleSendOTP = async () => {
    setSending(true);
    setError('');
    try {
      // Simulate sending OTP
      setTimeout(() => {
        setTimer(30);
        setSending(false);
      }, 500);
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
      setSending(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length < 6) {
      setError('Please enter a 6-digit OTP.');
      return;
    }

    setLoading(true);
    setError('');
    
    // Static verify logic as per user request
    setTimeout(() => {
      if (otp === '123456') {
        setIsVerified(true);
        setTimeout(() => {
          onSuccess();
          // Reset states for future uses
          setIsVerified(false);
          setOtp('');
          setLoading(false);
        }, 1000);
      } else {
        setError('Invalid OTP. Please try again.');
        setLoading(false);
      }
    }, 800);
  };

  return (
    <Dialog 
      open={open} 
      onClose={isVerified ? null : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 1 }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <OtpIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Security Verification</Typography>
        </Box>
        {!isVerified && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      
      <DialogContent sx={{ pb: 2 }}>
        {isVerified ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, textAlign: 'center' }}>
            <SuccessIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main', mb: 1 }}>Verified!</Typography>
            <Typography variant="body2" color="text.secondary">Redirecting you...</Typography>
          </Box>
        ) : (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please enter the OTP to proceed with <strong>{reason}</strong>.
              <Box component="span" sx={{ display: 'block', mt: 1, fontWeight: 700, color: 'primary.main' }}>
                (Hint: 123456)
              </Box>
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

            <TextField
              fullWidth
              variant="outlined"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              autoFocus
              inputProps={{ maxLength: 6, style: { textAlign: 'center', letterSpacing: '8px', fontSize: '24px', fontWeight: 'bold' } }}
              sx={{ mb: 2 }}
              onKeyPress={(e) => e.key === 'Enter' && handleVerifyOTP()}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">Didn't get code?</Typography>
              <Button 
                onClick={handleSendOTP} 
                disabled={timer > 0 || sending}
                size="small"
                sx={{ ml: 1, textTransform: 'none', fontWeight: 600 }}
              >
                {timer > 0 ? `Resend in ${timer}s` : 'Resend OTP'}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      {!isVerified && (
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            fullWidth 
            variant="contained" 
            onClick={handleVerifyOTP}
            disabled={loading || otp.length < 6}
            sx={{ py: 1.5, borderRadius: 2, fontWeight: 700, textTransform: 'none', fontSize: '1rem' }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Proceed'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default OTPVerificationModal;
